import React from 'react';
import dynamic from 'next/dynamic';
import {
  Icon,
  Heading,
  ArrowsDisplay,
  CarouselVariant,
  baseTheme,
} from '@feast-it/ui/light';
import { PATHS } from '../../constants/paths';
import { optimizeImageUrl } from '../../modules/imageLoader';

import { useBreakpoint } from '../../hooks/media';
import {
  Container,
  createStyledCarousel,
  Wrapper,
  BackgroundImage,
  Content,
  TextWrapper,
  Description,
  StyledLink,
  ArrowSpaceDivider,
} from './CrossSell.styled';

// Dynamic import for Carousel with styling
const StyledCarousel = dynamic(
  () =>
    import('@feast-it/ui/light').then(mod =>
      createStyledCarousel(mod.Carousel)
    ),
  {
    ssr: false, // Heavy carousel component - client-side only
    loading: () => <div>Loading carousel...</div>,
  }
);

interface CrossSellItem {
  heading: string;
  description: string;
  image: string;
  href: string;
  cta: string;
}

interface CrossSellProps {
  items: CrossSellItem[];
}

export const CrossSell: React.FC<CrossSellProps> = ({ items }) => {
  const isCarousel = items.length > 1;
  const isMobile = !useBreakpoint(baseTheme.breakpoints.medium);

  return (
    <Container>
      {isCarousel ? (
        <StyledCarousel
          isImageCarousel={false}
          perView={1}
          autoplay={6000}
          hoverpause
          arrowOptions={{
            variant: isMobile
              ? CarouselVariant.CENTER
              : CarouselVariant.BOTTOM_RIGHT,
            display: ArrowsDisplay.ALWAYS_ON,
            size: 38,
            color: 'black',
            buttonBackground: 'white',
            borderColor: 'lightGrey',
            middleOrnament: <ArrowSpaceDivider />,
          }}
          dotOptions={{
            display: false,
          }}
        >
          {items.map(item => (
            <Wrapper key={`cross-sell-${item.href}`}>
              <BackgroundImage
                backgroundImage={
                  item.image && item.image.includes('imgix.net')
                    ? optimizeImageUrl({
                        src: item.image,
                        width: 600,
                        quality: 70,
                      })
                    : item.image
                }
              />

              <Content>
                <TextWrapper>
                  <Heading as="h2" style="headingExtraLarge" color="black">
                    {item.heading}
                  </Heading>

                  <Description>{item.description}</Description>

                  <StyledLink
                    href={PATHS.listings(items[0].href)}
                    passHref="true"
                  >
                    {item.cta}
                    <Icon name="ArrowRightSmall" size={12} fill="mediumBlue" />
                  </StyledLink>
                </TextWrapper>
              </Content>
            </Wrapper>
          ))}
        </StyledCarousel>
      ) : (
        <Wrapper>
          <BackgroundImage
            backgroundImage={
              items[0].image && items[0].image.includes('imgix.net')
                ? optimizeImageUrl({
                    src: items[0].image,
                    width: 600,
                    quality: 70,
                  })
                : items[0].image
            }
          />

          <Content>
            <TextWrapper>
              <Heading as="h2" style="headingLarge" color="black">
                {items[0].heading}
              </Heading>

              <Description>{items[0].description}</Description>

              <StyledLink href={PATHS.listings(items[0].href)} passHref="true">
                {items[0].cta}
                <Icon name="ArrowRight" size={24} fill="black" />
              </StyledLink>
            </TextWrapper>
          </Content>
        </Wrapper>
      )}
    </Container>
  );
};
